import { useCallback, useRef } from 'react';

import { COUNTRY_CODES } from '@savgroup-front-common/constants';

import { DataGouvService } from '../../../../../api';
import { adaptDataGouvFeaturesToPlaces } from '../adapters/adaptDataGouvFeaturesToPlaces';
import { adaptGoogleSuggestionsToPlaces } from '../adapters/adaptGoogleSuggestionsToPlaces';
import { adaptPlaceDetailsToResponse } from '../adapters/adaptPlaceDetailsToResponse';
import createAutocompleteSession, {
  AutocompleteSession,
} from '../helpers/createAutocompleteSession';
import getPlacesLibrary from '../helpers/getPlacesLibrary';
import { defaultSearchOptions } from '../PlacesAutocomplete.constants';
import {
  Place,
  PlaceAutocompleteOnSelectArgs,
} from '../PlacesAutocomplete.types';

const useGoogleMapApi = ({ countryCode }: { countryCode: COUNTRY_CODES }) => {
  const sessionRef = useRef<AutocompleteSession | undefined>(undefined);

  if (!sessionRef.current) {
    sessionRef.current = createAutocompleteSession();
  }

  const getPlacesPredictions = useCallback(
    async ({
      input,
      includedRegionCodes,
    }: {
      input: string;
      includedRegionCodes?: string[];
    }): Promise<Place[]> => {
      if (countryCode === COUNTRY_CODES.FR) {
        if (!input) {
          throw new Error('Null input is provided');
        }

        const response = await DataGouvService.geoCodeAddressFromDataGouv({
          query: input,
        });

        return adaptDataGouvFeaturesToPlaces(response);
      }

      if (!input) {
        throw new Error('Null input is provided');
      }

      const placesLibrary = await getPlacesLibrary();
      const { suggestions } =
        await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(
          {
            ...defaultSearchOptions(),
            input,
            includedRegionCodes,
            sessionToken: sessionRef.current?.getToken(
              () => new placesLibrary.AutocompleteSessionToken(),
            ),
          },
        );

      return adaptGoogleSuggestionsToPlaces(suggestions);
    },
    [countryCode],
  );

  const resolveSelectedPlace = useCallback(
    async ({
      placeId,
      address,
    }: {
      placeId: string;
      address: string;
    }): Promise<PlaceAutocompleteOnSelectArgs> => {
      const placesLibrary = await getPlacesLibrary();
      const place = new placesLibrary.Place({ id: placeId });
      const sessionToken = sessionRef.current?.getToken(
        () => new placesLibrary.AutocompleteSessionToken(),
      );

      await place.fetchFields({
        fields: ['addressComponents', 'location'],
        sessionToken,
      });
      sessionRef.current?.reset();

      return adaptPlaceDetailsToResponse({
        addressComponents: place.addressComponents,
        location: place.location,
        address,
      });
    },
    [],
  );

  const resetSession = useCallback(() => {
    sessionRef.current?.reset();
  }, []);

  return {
    getPlacesPredictions,
    resolveSelectedPlace,
    resetSession,
  };
};

export default useGoogleMapApi;
